/**
 * Single y-partyserver connection to the per-user Durable Object.
 *
 * One WebSocket per signed-in user, shared by:
 *   - UserDo LWW sync (decks, cards, folders, settings) — via custom JSON
 *     messages routed through y-partyserver's `__YPS:` channel.
 *   - The ROOT Y.Doc (metadata only — schema version, future noteIndex).
 *     Per-note Y.Docs open their own per-active-note WebSocket via
 *     `useNoteDoc` and route to `?docKey=note\x01...` on the same DO.
 *
 * **`disableBc: true` is mandatory.** y-partyserver's BroadcastChannel
 * key is `${serverUrl}/${roomname}` and ignores query params; without
 * it, the root provider and any per-note providers in the same browser
 * share a channel and cross-pollinate updates intended for different
 * docs. See node_modules/y-partyserver/dist/provider/index.js:243.
 *
 * Gated on `userId`: when the user is signed out or still hydrating the
 * context exposes `null` and no network call is made.
 */

import React, { createContext, useContext, useMemo } from 'react';
import type YProvider from 'y-partyserver/provider';
import useYProvider from 'y-partyserver/react';

import { getPartykitHost } from '../internal/sync-config';
import { NoteDocSpawnProvider } from '../yjs/note-doc-spawn-context';

/**
 * Partyserver routes `/parties/<kebab-case-of-class-name>/<room>` to the
 * matching DO binding. Our class is `UserDo`, kebab-cased to `user-do`.
 */
const USER_DO_PARTY = 'user-do';

const UserDoConnectionContext = createContext<YProvider | null>(null);

interface UserDoConnectionProviderProps {
  userId: string | null | undefined;
  getToken?: () => Promise<string | null>;
  children: React.ReactNode;
}

export function UserDoConnectionProvider({
  userId,
  getToken,
  children,
}: UserDoConnectionProviderProps): React.ReactElement {
  if (!userId) {
    return (
      <UserDoConnectionContext.Provider value={null}>
        <NoteDocSpawnProvider value={null}>{children}</NoteDocSpawnProvider>
      </UserDoConnectionContext.Provider>
    );
  }
  return (
    <ConnectedUserDoConnectionProvider userId={userId} getToken={getToken}>
      {children}
    </ConnectedUserDoConnectionProvider>
  );
}

function ConnectedUserDoConnectionProvider({
  userId,
  getToken,
  children,
}: {
  userId: string;
  getToken?: () => Promise<string | null>;
  children: React.ReactNode;
}): React.ReactElement {
  const partykitHost = getPartykitHost();
  const provider = useYProvider({
    host: partykitHost,
    party: USER_DO_PARTY,
    room: userId,
    options: {
      // CRITICAL — see file header. Without disableBc the root
      // provider's BroadcastChannel collides with every per-note
      // provider sharing this room.
      disableBc: true,
      ...(getToken
        ? {
            params: async () => {
              const token = await getToken();
              return token ? { token } : {};
            },
          }
        : {}),
    },
  });

  // Spawn config for `useNoteDoc` consumers further down the tree —
  // personal-scope notes route to `user-do` rooms, with the userId as
  // the room name. Memoised so the value identity is stable across
  // renders that don't change the inputs.
  const spawnConfig = useMemo(
    () => ({
      party: USER_DO_PARTY,
      room: userId,
      host: partykitHost,
      getToken,
    }),
    [userId, getToken, partykitHost],
  );

  return (
    <UserDoConnectionContext.Provider value={provider}>
      <NoteDocSpawnProvider value={spawnConfig}>{children}</NoteDocSpawnProvider>
    </UserDoConnectionContext.Provider>
  );
}

/**
 * The live y-partyserver provider for the current user, or `null` when
 * signed out / not yet hydrated.
 */
export function useUserDoConnection(): YProvider | null {
  return useContext(UserDoConnectionContext);
}
